import React from "react";
import ApiKeyContext from "../contexts/ApiKeyContext";
import CatalogContext from "../contexts/CatalogContext";
import Proposal from "../models/Proposal";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const ProposalContext = React.createContext({
	proposals: null,
	/** @type Proposal */ currentProposal: null,
	flag: false,
	proposalsLoading: false,
	setProposals: () => { },
	getProposals: () => { },
	setCurrentProposal: () => { },
	setAsCurrentProposal: () => { },
	fetchAndSetCurrent: () => { },
	setCurrentProposalCacheRef: () => { },
	clearCurrentProposalCacheRef: () => { },
	createNewProposal: () => { },
	forceUpdateRerenderFlag: () => { },
	getAwaitingProposalItem: () => { },
	setAwaitingProposalItem: value => { },
});

export const useProposalContext = () => {
	const context = React.useContext(ProposalContext);
	if (!context) {
		throw new Error(
			"useProposalContext must be used within a ProposalContextProvider"
		);
	}
	return context;
};

export const ProposalContextProvider = ({ children }) => {
	const apiContext = React.useContext(ApiKeyContext);
	const [catalog] = React.useContext(CatalogContext);

	const currentProposalCacheRef = catalog?.account?.code
		+ '/' + apiContext.apiKey
		+ '/proposal/current';

	const storedProposalId = localStorage.getItem(currentProposalCacheRef);
	const [proposals, setProposals] = React.useState(null);
	const [proposalsLoading, setProposalsLoading] = React.useState(false);
	const awaitingProposalItem = React.useRef();

	const [currentProposal, setCurrentProposal] = React.useState(null);
	const [currentProposalLoading, setCurrentProposalLoading] = React.useState(null);

	const [flag, setFlag] = React.useState(false);

	const Dialog = withReactContent(Swal);

	const proposalLoading = React.useRef(undefined);
	
	const fetchAndSetCurrent = async proposalId => {
		if (proposalLoading.current) {
			return proposalLoading.current;
		}
		
		if (proposalId) {
			localStorage.setItem(currentProposalCacheRef, proposalId);
			setProposalsLoading(true);
			try {
				proposalLoading.current = Proposal.FromId(proposalId);
				const proposal = await proposalLoading.current.then(p => {
					proposalLoading.current = undefined;
					return p;
				});
				setProposalsLoading(false);
				setCurrentProposal(proposal);
				setFlag(flag => !flag);
				return proposal;
			} catch (e) {
				console.error("error loading proposal: ", e);
				setProposalsLoading(false);
				proposalLoading.current = undefined;
			}
		}
	};
	
	let getProposals_promise;

	const getProposals = React.useCallback(() => {
		//if no proposal access
		if(!catalog.options?.proposals?.access){
			return;
		}
		if (getProposals_promise) {
			return getProposals_promise;
		}

		setProposalsLoading(true);
		
		getProposals_promise = Proposal.SearchBy({ order: ['{updated_on} DESC'] }).then(response => {
			setProposals(response);
			setProposalsLoading(false);
		}).catch((e)=>{
			console.error("error searching proposals: ", e);
			setProposalsLoading(false);
		}).finally(() => {
			getProposals_promise = undefined;
		});
		
	}, [catalog.options?.proposals?.access]);

	React.useEffect(() => {
		if(!proposals)
		getProposals();
	}, [proposals, getProposals]);

	const getProposal = React.useCallback(() => {
		!currentProposalLoading
			&& storedProposalId
			&& storedProposalId !== "undefined"
			&& +storedProposalId !== +currentProposal?.id
			&& Proposal.Load(apiContext, storedProposalId, null, { cache: false })
				.then(proposal => setCurrentProposal(proposal))
				.catch(console.error);

		setCurrentProposalLoading(true);

		return true;
	}, [
		apiContext,
		currentProposal?.id,
		currentProposalLoading,
		storedProposalId,
	]);

	React.useEffect(() => {
		catalog.options?.proposals?.access
			// && catalog?.options?.type !== 'public'
			&& getProposal();
	}, [catalog.options?.proposals?.access, getProposal]);

	const setAsCurrentProposal = (proposal) => {
		proposal?.id
			? localStorage.setItem(currentProposalCacheRef, proposal.id)
			: localStorage.removeItem(currentProposalCacheRef);

		setCurrentProposal(proposal);
		return true;
	};

	const setCurrentProposalCacheRef = (proposalId) => {
		localStorage.setItem(currentProposalCacheRef, proposalId);
	};

	const clearCurrentProposalCacheRef = () => {
		localStorage.removeItem(currentProposalCacheRef);
		getProposals();
		setCurrentProposal(null);
	};

	const createNewProposal = async () => {
		const result = await Dialog.fire({
			title: "Create Order",
			input: "text",
			inputLabel: "Type new order name",
			inputValidator: (value) => {
				if (!value) {
					return "Please enter a order name";
				}
			},
			showClass: {
				popup: "proposal-new",
			},
			inputAttributes: {
				autoComplete: "off",
			},
			showCancelButton: true,
			confirmButtonText: "Save",
			reverseButtons: true,
		});

		if (!result.isConfirmed) {
			return;
		}
		const newProposal = new Proposal();
		newProposal.name = result.value;

		await newProposal.save();
		newProposal.items = [];

		setProposals(prevProposals => [newProposal, ...prevProposals]);
		setAsCurrentProposal(newProposal);

		return newProposal;
	};

	// force-update re-render component
	const forceUpdateRerenderFlag = () => {
		setFlag(flag => !flag);
		return true;
	};

	const getAwaitingProposalItem = () => awaitingProposalItem.current;
	const setAwaitingProposalItem = value => {
		awaitingProposalItem.current = value;
	}

	const value = {
		proposals,
		currentProposal,
		flag,
		proposalsLoading,
		setProposals,
		getProposals,
		setCurrentProposal,
		setAsCurrentProposal,
		fetchAndSetCurrent,
		setCurrentProposalCacheRef,
		clearCurrentProposalCacheRef,
		createNewProposal,
		forceUpdateRerenderFlag,
		getAwaitingProposalItem,
		setAwaitingProposalItem,
	};

	return (
		<ProposalContext.Provider value={value} displayName="Proposal Context">
			{children}
		</ProposalContext.Provider>
	);
};
